import React, { useCallback, useMemo } from 'react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { useNotification } from '../hooks/useNotification';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const CompanySwitcher = () => {
  const {
    currentCompany,
    userCompanies,
    loading,
    switchCompany,
    loadUserCompanies,
  } = useEnhancedCompany();
  const notify = useNotification();
  const [switching, setSwitching] = React.useState(false);

  const handleCompanySwitch = useCallback(async (companyId) => {
    if (switching || !companyId || companyId === currentCompany?.companyId) {
      return;
    }

    if (!switchCompany) {
      notify.error('Company switching is not available. Please refresh the page.');
      return;
    }

    const targetCompany = userCompanies?.find((c) => c.companyId === companyId);
    if (!targetCompany) {
      notify.error('Company not found in your companies list');
      return;
    }

    setSwitching(true);
    try {
      await switchCompany(companyId);
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      notify.error(`Failed to switch company: ${errorMessage}`, { title: 'Switch Failed' });
    } finally {
      setSwitching(false);
    }
  }, [switching, currentCompany?.companyId, switchCompany, userCompanies, notify]);

  const companiesArray = useMemo(
    () => (Array.isArray(userCompanies) ? userCompanies : []),
    [userCompanies]
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'offline': return '⚫';
      case 'suspended': return '🟡';
      case 'removed': return '🔴';
      default: return '⚫';
    }
  };

  const companyLabel = (company) =>
    company.details?.firmName ||
    company.details?.name ||
    company.name ||
    company.companyId;

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
        <span className="text-sm text-gray-600">Loading companies...</span>
      </div>
    );
  }

  if (companiesArray.length === 0) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-500">No companies found</span>
        <button
          onClick={async () => {
            try {
              const { auth, db } = await import('../firebase.config');
              const { doc, setDoc } = await import('firebase/firestore');
              const uid = auth.currentUser?.uid;
              if (uid && db) {
                const { getDocs, query, where, collection } = await import('firebase/firestore');
                const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', uid));
                const companiesSnap = await getDocs(companiesQuery);

                const ownedCompanies = [];
                companiesSnap.forEach((docSnap) => {
                  ownedCompanies.push({
                    companyId: docSnap.id,
                    role: 'owner',
                    status: 'active',
                    addedAt: new Date(),
                  });
                });

                if (ownedCompanies.length === 0) return;

                const userCompaniesRef = doc(db, 'user_companies', uid);
                await setDoc(userCompaniesRef, {
                  owned_companies: ownedCompanies,
                  employed_companies: [],
                  active_company: {
                    companyId: ownedCompanies[0].companyId,
                    type: 'owned',
                    switched_at: new Date(),
                  },
                  last_updated: new Date(),
                });
                if (loadUserCompanies) {
                  await loadUserCompanies(uid);
                }
              }
            } catch (e) {
              console.error('Manual company setup failed:', e);
            }
          }}
          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Setup Companies
        </button>
      </div>
    );
  }

  if (companiesArray.length === 1) {
    const company = companiesArray[0];
    return (
      <div className="flex items-center space-x-2 min-w-0">
        <span className="text-sm shrink-0">{getStatusIcon(company.status)}</span>
        <span className="text-sm font-medium truncate">{companyLabel(company)}</span>
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 shrink-0">
          {company.role}
        </span>
      </div>
    );
  }

  const selectOptions = companiesArray.map((company) => {
    const isCurrent = company.companyId === currentCompany?.companyId;
    return {
      value: company.companyId,
      label: `${getStatusIcon(company.status)} ${companyLabel(company)}${isCurrent ? ' (Current)' : ''} · ${company.role}`,
    };
  });

  return (
    <AppSelect
      value={currentCompany?.companyId || ''}
      onChange={(e) => handleCompanySwitch(e.target.value)}
      disabled={switching}
      aria-label="Switch company"
      sheetTitle="Switch Company"
      placeholder={switching ? 'Switching…' : 'Select Company'}
      options={selectOptions}
      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} shadow-sm`}
    />
  );
};

export default CompanySwitcher;
